import { QbsTable } from 'qbs-react-grid'
import React, { useCallback, useEffect, useState } from 'react'

import { TableColumns } from '../../../common/types'
import Icons from '../../../components/common/icons'
import { calcWindowHeight } from '../../../utilities/calcHeight'
import { handleReturnEmptyMsg } from '../../../utilities/validation'
import { getAccreditationCycleReport } from '../api'

const formatDate = (value?: string) => {
  if (!value) return '--'
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const defaultColumnProps = {
  sortable: false,
  resizable: true,
  isVisible: true,
}

const columns: TableColumns[] = [
  {
    title: 'Organisation',
    field: 'organisation',
    colWidth: 220,
    ...defaultColumnProps,
  },
  { title: 'Tier', field: 'tier', colWidth: 150, ...defaultColumnProps },
  {
    title: 'Application Type',
    field: 'application_type',
    colWidth: 170,
    ...defaultColumnProps,
  },
  {
    title: 'Initial Submission',
    field: 'initial_submission_date',
    colWidth: 180,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: <>{formatDate(rowData.initial_submission_date)}</>,
    }),
  },
  {
    title: 'Latest Submission',
    field: 'latest_submission_date',
    colWidth: 180,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: <>{formatDate(rowData.latest_submission_date)}</>,
    }),
  },
  {
    title: 'Accreditation Decision',
    field: 'accreditation_decision_date',
    colWidth: 200,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: <>{formatDate(rowData.accreditation_decision_date)}</>,
    }),
  },
  {
    title: 'Cycle Time',
    field: 'total_cycle_time_days',
    colWidth: 130,
    ...defaultColumnProps,
    customCell: true,
    renderCell: (rowData) => ({
      cell: (
        <>
          {rowData.total_cycle_time_days !== null &&
          rowData.total_cycle_time_days !== undefined
            ? `${rowData.total_cycle_time_days} days`
            : '--'}
        </>
      ),
    }),
  },
  {
    title: 'Submissions',
    field: 'number_of_submissions',
    colWidth: 130,
    ...defaultColumnProps,
  },
  {
    title: 'Refinements',
    field: 'number_of_refinements',
    colWidth: 130,
    ...defaultColumnProps,
  },
  {
    title: 'Reworks',
    field: 'number_of_reworks',
    colWidth: 120,
    ...defaultColumnProps,
  },
  {
    title: 'Returned To Organisation',
    field: 'returned_to_organisation_count',
    colWidth: 210,
    ...defaultColumnProps,
  },
  {
    title: 'Time Between Submissions',
    field: 'time_between_submissions',
    colWidth: 220,
    ...defaultColumnProps,
  },
  {
    title: 'Time In Each Status',
    field: 'time_spent_in_stages',
    colWidth: 280,
    ...defaultColumnProps,
  },
  {
    title: 'Current Application Status',
    field: 'current_application_status',
    colWidth: 210,
    ...defaultColumnProps,
  },
]

export default function AccreditationCycleReport({
  searchParams,
  pageParams,
  onChangePage,
  onChangeRowsPerPage,
  handleSeach,
}: any) {
  const [data, setData] = useState<any>([])
  const tableData = Array.isArray(data) ? data : data?.items ?? []
  const totalRecords = Array.isArray(data) ? data.length : data?.total

  const getData = useCallback(async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { filters, filterProps, ...restSearchParams } = pageParams
    const response = await getAccreditationCycleReport({
      ...searchParams,
      ...restSearchParams,
    })
    setData(response)
  }, [pageParams, searchParams])

  useEffect(() => {
    getData()
  }, [getData])

  return (
    <div className="p-4">
      <QbsTable
        data={tableData}
        dataRowKey="id"
        toolbar={true}
        height={calcWindowHeight(335)}
        emptyTitle="No records to display"
        emptySubTitle={handleReturnEmptyMsg(pageParams?.search)}
        columns={columns}
        pagination={true}
        renderSortIcon={(sortType?: 'asc' | 'desc' | undefined) => {
          return sortType === 'asc' ? (
            <Icons name="ascending-icon" />
          ) : sortType === 'desc' ? (
            <Icons name="descending-icon" />
          ) : (
            <Icons name="qbs-sort-icon" />
          )
        }}
        paginationProps={{
          onPagination: onChangePage,
          total: totalRecords,
          currentPage: pageParams?.page,
          rowsPerPage: Number(pageParams?.page_size ?? data?.page_size),
          onRowsPerPage: onChangeRowsPerPage,
          dropOptions: [10, 20, 30, 50, 100],
        }}
        searchValue={pageParams?.search}
        onSearch={handleSeach}
        asyncSearch
        columnToggle
      />
    </div>
  )
}
